import { Component, OnInit, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { environment } from '../core/environment';

interface AttendanceRow {
  id: string;
  personType: number;
  personId: string;
  attendanceDate: string;
  inTime?: string;
  outTime?: string;
  status: number;
  method: number;
  remarks?: string;
}

@Component({
  selector: 'app-attendance',
  standalone: true,
  imports: [ReactiveFormsModule],
  template: `
    <div class="attendance-simple">
      <section class="attendance-hero">
        <div>
          <span class="eyebrow">Daily attendance</span>
          <h2>Entry & Exit Desk</h2>
          <p>Scan QR/card for instant in-out marking. Use manual entry only when scan is not available.</p>
        </div>
        <button type="button" (click)="load()">Refresh</button>
      </section>

      <section class="attendance-summary">
        <article><span>Total Logs</span><strong>{{ rows().length }}</strong></article>
        <article><span>Inside Now</span><strong>{{ checkedInCount() }}</strong></article>
        <article><span>Checked Out</span><strong>{{ checkedOutCount() }}</strong></article>
        <article><span>Manual</span><strong>{{ manualCount() }}</strong></article>
      </section>

      <section class="attendance-main">
        <form class="scan-desk-card" [formGroup]="scanForm" (ngSubmit)="scan()">
          <div>
            <span>01</span>
            <h3>Scan Member Card</h3>
            <p>First scan marks In. Second scan marks Out.</p>
          </div>
          <label>
            Card / QR Number
            <input formControlName="cardNumber" placeholder="Scan or type card number" autofocus>
          </label>
          <button type="submit" [disabled]="scanForm.invalid">Mark In / Out</button>

          @if (scanResult()) {
            <div class="simple-scan-result" [class.good]="scanResult()?.allowed">
              <strong>{{ scanResult()?.allowed ? 'Access allowed' : 'Access blocked' }}</strong>
              <span>{{ scanResult()?.message }}</span>
            </div>
          }
        </form>

        <form class="manual-desk-card" [formGroup]="manualForm" (ngSubmit)="markManual()">
          <div class="simple-card-heading">
            <div>
              <span>02</span>
              <h3>Manual Mark</h3>
              <p>For no-card cases.</p>
            </div>
          </div>

          <div class="quick-toggle">
            <label><input type="radio" formControlName="personType" [value]="1"> Student</label>
            <label><input type="radio" formControlName="personType" [value]="2"> Staff</label>
            <label><input type="radio" formControlName="personType" [value]="3"> Guest</label>
          </div>

          <label>Person Id
            <input formControlName="personId" placeholder="Paste student/staff/guest id">
          </label>

          <div class="two-col">
            <label>Status
              <select formControlName="status">
                <option [value]="1">Present</option>
                <option [value]="2">Absent</option>
                <option [value]="3">Late</option>
              </select>
            </label>
            <label>Date
              <input type="date" formControlName="attendanceDate">
            </label>
          </div>

          <button type="submit" [disabled]="manualForm.invalid">Save Manual Entry</button>
        </form>
      </section>

      <article class="attendance-log-card">
        <div class="simple-card-heading">
          <div>
            <span>Live</span>
            <h3>Recent Movement</h3>
            <p>Latest in-time and out-time records.</p>
          </div>
        </div>

        <div class="table-scroll">
          <table>
            <thead>
              <tr><th>Date</th><th>Type</th><th>Person Id</th><th>In</th><th>Out</th><th>Status</th><th>Method</th></tr>
            </thead>
            <tbody>
              @for (row of rows(); track row.id) {
                <tr>
                  <td>{{ row.attendanceDate }}</td>
                  <td>{{ personTypeLabel(row.personType) }}</td>
                  <td>{{ row.personId }}</td>
                  <td>{{ row.inTime || '-' }}</td>
                  <td>{{ row.outTime || '-' }}</td>
                  <td><span class="status-chip">{{ statusLabel(row.status) }}</span></td>
                  <td>{{ row.method === 1 ? 'Manual' : 'QR/Card' }}</td>
                </tr>
              } @empty {
                <tr><td colspan="7"><div class="guest-empty">No attendance logs yet today.</div></td></tr>
              }
            </tbody>
          </table>
        </div>
      </article>
    </div>
  `
})
export class AttendanceComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly fb = inject(FormBuilder);
  readonly rows = signal<AttendanceRow[]>([]);
  readonly scanResult = signal<{ allowed: boolean; message: string } | null>(null);
  readonly scanForm = this.fb.nonNullable.group({ cardNumber: ['', Validators.required] });
  readonly manualForm = this.fb.nonNullable.group({
    personType: [1, Validators.required],
    personId: ['', Validators.required],
    attendanceDate: [new Date().toISOString().slice(0, 10), Validators.required],
    status: [1, Validators.required],
    remarks: ['']
  });

  ngOnInit(): void { this.load(); }
  checkedInCount(): number { return this.rows().filter((x) => x.inTime && !x.outTime).length; }
  checkedOutCount(): number { return this.rows().filter((x) => x.outTime).length; }
  manualCount(): number { return this.rows().filter((x) => x.method === 1).length; }

  scan(): void {
    this.http.post<{ allowed: boolean; message: string }>(`${environment.apiUrl}/qr/scan`, this.scanForm.getRawValue()).subscribe((result) => {
      this.scanResult.set(result);
      this.scanForm.reset();
      this.load();
    });
  }

  markManual(): void {
    this.http.post(`${environment.apiUrl}/attendance/manual`, this.manualForm.getRawValue()).subscribe(() => {
      this.manualForm.patchValue({ personId: '', remarks: '' });
      this.load();
    });
  }

  load(): void {
    this.http.get<AttendanceRow[]>(`${environment.apiUrl}/attendance/daily`).subscribe((rows) => this.rows.set(rows));
  }

  personTypeLabel(type: number): string { return type === 1 ? 'Student' : type === 2 ? 'Staff' : 'Guest'; }
  statusLabel(status: number): string { return status === 1 ? 'Present' : status === 2 ? 'Absent' : status === 3 ? 'Late' : 'Checked Out'; }
}
